import axios from "axios";
import { getAuthHeader } from "./auth";
import {
  DEFAULT_CANDIDATE_OFFICE,
  normalizeCandidateOffice,
  shouldShowDistrictForCandidateOffice,
} from "./fecCandidateFilters";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

export {
  CANDIDATE_OFFICE_OPTIONS,
  CANDIDATE_OFFICE_H,
  CANDIDATE_OFFICE_S,
  CANDIDATE_OFFICE_P,
  DEFAULT_CANDIDATE_OFFICE,
  getVisibleCandidateOfficeOptions,
  getCandidateOfficeLabel,
  isPresidentialElectionYear,
  normalizeCandidateOffice,
  shouldShowDistrictForCandidateOffice,
  shouldSuggestStateFilterForCandidates,
} from "./fecCandidateFilters";

// ============================================================================
// USER SEARCH
// ============================================================================

export async function searchUsers(query, page = 1, pageSize = 20, accountType = null, state = null, district = null) {
  const headers = await getAuthHeader();
  const params = { q: query || "", page, page_size: pageSize };
  if (accountType) params.account_type = accountType;
  if (state) params.state = state;
  if (district) params.district = district;

  const response = await axios.get(`${API_BASE_URL}/api/users/search/`, { headers, params });
  return response.data;
}

// ============================================================================
// CONGRESS MEMBER SEARCH
// ============================================================================

export async function searchCongressMembers(filters = {}) {
  const headers = await getAuthHeader();
  const params = {
    page: filters.page || 1,
    page_size: filters.pageSize || 20,
  };
  if (filters.query) params.q = filters.query;
  if (filters.state) params.state = filters.state;
  if (filters.district) params.district = filters.district;
  if (filters.position) params.position = filters.position;

  const response = await axios.get(`${API_BASE_URL}/api/congress/search/`, { headers, params });
  return response.data;
}

/** Get a single congress member by bioguide_id. Public, no auth required. */
export async function fetchCongressMemberByBioguideId(bioguideId) {
  const response = await axios.get(`${API_BASE_URL}/api/congress/member/${encodeURIComponent(bioguideId)}/`);
  return response.data;
}

// ============================================================================
// FEC CANDIDATES (via backend proxy)
// ============================================================================

export async function fetchFECCandidates(filters = {}) {
  const headers = await getAuthHeader();
  const params = {
    page: filters.page || 1,
    page_size: filters.pageSize || 20,
  };
  if (filters.query) params.q = filters.query;
  if (filters.state) params.state = filters.state;
  if (filters.district) params.district = filters.district;
  if (filters.office) params.office = filters.office;

  const response = await axios.get(`${API_BASE_URL}/api/fec/candidates/`, { headers, params, timeout: 15000 });
  return response.data;
}

/**
 * Normalize office/state/district for FEC candidate list requests.
 * District applies only to House; single-district states omit district param.
 */
export function buildFECCandidateListParams({
  office = DEFAULT_CANDIDATE_OFFICE,
  state = null,
  district = null,
  query = null,
  page = 1,
  pageSize = 20,
} = {}) {
  const officeCode = normalizeCandidateOffice(office);
  let districtFilter = district || undefined;

  if (!shouldShowDistrictForCandidateOffice(officeCode)) {
    districtFilter = undefined;
  } else if (state && STATE_DISTRICT_MAP[state] === 1) {
    districtFilter = undefined;
  }

  return {
    office: officeCode,
    state: state || undefined,
    district: districtFilter,
    query: query || undefined,
    page,
    pageSize,
  };
}

// ============================================================================
// USER PROFILE
// ============================================================================

export async function getUserProfile(userIdOrUsername) {
  const headers = await getAuthHeader();
  const identifier = encodeURIComponent(String(userIdOrUsername || "").trim());
  const response = await axios.get(`${API_BASE_URL}/api/users/${identifier}/public-profile/`, { headers });
  return response.data;
}

export async function fetchUserBadgesByUserId(userId) {
  const headers = await getAuthHeader();
  const response = await axios.get(`${API_BASE_URL}/api/users/${userId}/badges/`, { headers });
  return response.data;
}

// ============================================================================
// CALL LEADERBOARD
// ============================================================================

export async function fetchCallLeaderboard(page = 1, pageSize = 20, { verifiedOnly = false } = {}) {
  const headers = await getAuthHeader();
  const response = await axios.get(`${API_BASE_URL}/api/leaderboard/calls/`, {
    headers,
    params: {
      page,
      page_size: pageSize,
      ...(verifiedOnly ? { verified_only: 'true' } : {}),
    },
  });
  return response.data;
}

export async function fetchUserCallActivity(userId) {
  const headers = await getAuthHeader();
  const response = await axios.get(`${API_BASE_URL}/api/users/${userId}/call-activity/`, { headers });
  return response.data;
}

export async function getLeaderboardOptIn() {
  const headers = await getAuthHeader();
  const response = await axios.get(`${API_BASE_URL}/api/leaderboard/opt-in/`, { headers });
  return response.data;
}

export async function setLeaderboardOptIn(optedIn) {
  const headers = await getAuthHeader();
  const response = await axios.post(
    `${API_BASE_URL}/api/leaderboard/opt-in/`,
    { opted_in: optedIn },
    { headers },
  );
  return response.data;
}

// ============================================================================
// FOLLOW / UNFOLLOW
// ============================================================================

export async function followUser(userId) {
  const headers = await getAuthHeader();
  const response = await axios.post(`${API_BASE_URL}/api/users/${userId}/follow/`, {}, { headers });
  return response.data;
}

export async function unfollowUser(userId) {
  const headers = await getAuthHeader();
  const response = await axios.delete(`${API_BASE_URL}/api/users/${userId}/follow/`, { headers });
  return response.data;
}

// ============================================================================
// BLOCK / UNBLOCK
// ============================================================================

/** Shared confirmation copy so every block prompt promises the same behavior. */
export const BLOCK_CONFIRM_DESCRIPTION =
  "Their posts and comments will be hidden from you across CivixThread, and you'll stop following " +
  "each other. They can still see your public activity. You can undo this from Account settings.";

/** Turn block/unblock API failures into copy users can act on. */
export function getBlockActionErrorMessage(err, fallback = "Something went wrong. Please try again.") {
  const status = err?.response?.status;
  const apiError = err?.response?.data?.error;

  if (status === 401) {
    return "Please sign in to manage blocked accounts.";
  }
  if (status === 403) {
    return apiError || "You don't have permission to do that.";
  }
  if (!err?.response) {
    return "We couldn't reach CivixThread. Check your connection and try again.";
  }
  if (apiError) {
    return apiError;
  }
  return fallback;
}

/** Follow failures that stem from an existing block relationship. */
export function getFollowBlockedErrorMessage(err, fallback = "Couldn't update follow status. Please try again.") {
  const apiError = err?.response?.data?.error;
  if (apiError === "You've blocked this account. Unblock them first if you'd like to follow them again.") {
    return apiError;
  }
  if (apiError === "You can't follow this account right now.") {
    return apiError;
  }
  return getBlockActionErrorMessage(err, fallback);
}

/** Block a user. Hides their posts and comments from you and drops both follows. */
export async function blockUser(userId) {
  try {
    const headers = await getAuthHeader();
    const response = await axios.post(`${API_BASE_URL}/api/users/${userId}/block/`, {}, { headers });
    return response.data;
  } catch (err) {
    throw new Error(getBlockActionErrorMessage(err, "Couldn't block this account. Please try again."));
  }
}

export async function unblockUser(userId) {
  try {
    const headers = await getAuthHeader();
    const response = await axios.delete(`${API_BASE_URL}/api/users/${userId}/block/`, { headers });
    return response.data;
  } catch (err) {
    throw new Error(getBlockActionErrorMessage(err, "Couldn't unblock this account. Please try again."));
  }
}

/** Users the signed-in account has blocked, newest first. */
export async function getBlockedUsers() {
  try {
    const headers = await getAuthHeader();
    const response = await axios.get(`${API_BASE_URL}/api/users/blocked/`, { headers });
    return response.data;
  } catch (err) {
    throw new Error(getBlockActionErrorMessage(err, "Couldn't load your blocked accounts. Please try again."));
  }
}

// ============================================================================
// DATA MAPPERS
// ============================================================================

export const ORGANIZATION_SUBTYPE_LABELS = {
  nonprofit: "Nonprofit",
  business: "Business",
  community: "Community",
  campaign: "Campaign",
  union: "Union",
  advocacy: "Advocacy",
  other: "Org",
};

export function getOrganizationSubtypeLabel(subtype) {
  if (!subtype) return null;
  return ORGANIZATION_SUBTYPE_LABELS[subtype] || subtype;
}

/** Match public profile naming: org/municipality names before personal names. */
export function getDiscoverDisplayName(apiUser) {
  const entityName = (apiUser.entity_name || "").trim();
  if (entityName) return entityName;
  const municipalityName = (apiUser.municipality_name || "").trim();
  if (municipalityName) return municipalityName;
  if (apiUser.first_name && apiUser.last_name) {
    return `${apiUser.first_name} ${apiUser.last_name}`;
  }
  return apiUser.username || "User";
}

export function mapApiUserToSummary(apiUser) {
  const displayName = getDiscoverDisplayName(apiUser);

  let districtLabel = null;
  if (apiUser.state) {
    if (apiUser.district_number && apiUser.district_number !== "N/A") {
      districtLabel = `${apiUser.state}-${apiUser.district_number}`;
    } else {
      districtLabel = apiUser.state;
    }
  }

  return {
    id: apiUser.id,
    displayName,
    username: apiUser.username || null,
    avatarUrl: apiUser.profile_image || null,
    isVerified: apiUser.is_verified || false,
    districtLabel,
    state: apiUser.state || null,
    followerCount: apiUser.followers_count || 0,
    bio: apiUser.bio || null,
    viewerIsFollowing: apiUser.is_following || false,
    viewerHasBlocked: apiUser.viewer_has_blocked || false,
    accountType: apiUser.account_type?.name || "citizen",
    organizationSubtype: apiUser.organization_subtype || null,
  };
}

export function mapCongressMemberToSummary(member, position) {
  // Match mobile: only use bioguide_id for navigation; do not use member.id (may be DB pk and cause 500)
  const bioguideId = member.bioguide_id ?? member.bioguideId;
  return {
    id: member.bioguide_id ?? member.bioguideId ?? member.id,
    bioguideId: bioguideId || undefined,
    displayName: member.full_name || `${member.first_name} ${member.last_name}`,
    username: null,
    avatarUrl: member.bio_photo_url || null,
    isVerified: member.has_civixthread_account || false,
    districtLabel: member.recent_district
      ? `${member.recent_state}-${member.recent_district}`
      : member.recent_state || null,
    state: member.recent_state || null,
    followerCount: 0,
    bio: null,
    viewerIsFollowing: false,
    accountType: position === "senate" ? "senator" : "representative",
    party: member.party,
    phone: member.phone_number,
    website: member.official_website_url,
    hasCivixThreadAccount: member.has_civixthread_account || false,
    civixThreadUserId: member.civixthread_user_id || null,
  };
}

export function mapFECCandidateToDisplay(fecCandidate) {
  let districtLabel = "";
  if (fecCandidate.state) {
    if (fecCandidate.district && fecCandidate.district !== "AL") {
      districtLabel = `${fecCandidate.state}-${fecCandidate.district}`;
    } else if (fecCandidate.office_code === "H") {
      districtLabel = `${fecCandidate.state}-AL`;
    } else {
      districtLabel = fecCandidate.state;
    }
  }

  return {
    id: fecCandidate.id || fecCandidate.candidate_id,
    displayName: fecCandidate.name || "Unknown Candidate",
    party: fecCandidate.party || "IND",
    office: fecCandidate.office || "House",
    officeCode: fecCandidate.office_code || fecCandidate.office || "H",
    state: fecCandidate.state || "",
    district: fecCandidate.district || "",
    incumbent: fecCandidate.incumbent || false,
    openSeat: fecCandidate.open_seat || false,
    website: fecCandidate.website || null,
    districtLabel,
    totalRaised: fecCandidate.total_receipts || null,
    hasCivixThreadAccount: fecCandidate.has_civixthread_account || false,
    civixThreadUserId: fecCandidate.civixthread_user_id || null,
    viewerIsFollowing: false,
  };
}

// ============================================================================
// CONSTANTS
// ============================================================================

export const CIVIX_USER_ACCOUNT_TYPE = "civixthread_user";

export const FILTER_OPTIONS = [
  { id: "all", label: "All", accountTypeName: null, hidden: true },
  { id: "representatives", label: "Representatives", accountTypeName: "representative" },
  { id: "senators", label: "Senators", accountTypeName: "senator" },
  { id: "candidates", label: "Candidates", accountTypeName: "candidate" },
  { id: "civixthread_users", label: "On CivixThread", accountTypeName: CIVIX_USER_ACCOUNT_TYPE },
];

export const VISIBLE_FILTER_OPTIONS = FILTER_OPTIONS.filter((option) => !option.hidden);

export const LOGIN_REQUIRED_FILTERS = ["civixthread_users"];

export const ACCOUNT_TYPE_LABELS = {
  citizen: "Citizen",
  municipality: "Municipality",
  organization: "Organization",
  public_official_candidate: "Public Official",
};

const DISCOVER_SEARCH_PLACEHOLDERS = {
  representative: "Search representatives…",
  senator: "Search senators…",
  candidate: "Search candidates…",
  [CIVIX_USER_ACCOUNT_TYPE]: "Search people and organizations on CivixThread",
};

export function getDiscoverSearchPlaceholder(accountTypeName) {
  return DISCOVER_SEARCH_PLACEHOLDERS[accountTypeName]
    || DISCOVER_SEARCH_PLACEHOLDERS.representative;
}

export const US_STATES = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" }, { code: "DC", name: "District of Columbia" },
];

export const STATE_DISTRICT_MAP = {
  AL: 7, AK: 1, AZ: 9, AR: 4, CA: 52, CO: 8, CT: 5, DE: 1,
  FL: 28, GA: 14, HI: 2, ID: 2, IL: 17, IN: 9, IA: 4, KS: 4,
  KY: 6, LA: 6, ME: 2, MD: 8, MA: 9, MI: 13, MN: 8, MS: 4,
  MO: 8, MT: 2, NE: 3, NV: 4, NH: 2, NJ: 12, NM: 3, NY: 26,
  NC: 14, ND: 1, OH: 15, OK: 5, OR: 6, PA: 17, RI: 2, SC: 7,
  SD: 1, TN: 9, TX: 38, UT: 4, VT: 1, VA: 11, WA: 10, WV: 2,
  WI: 8, WY: 1, DC: 1,
};

export function getDistrictOptions(stateCode) {
  if (!stateCode || !STATE_DISTRICT_MAP[stateCode]) return [];
  const count = STATE_DISTRICT_MAP[stateCode];
  const districts = [];
  for (let i = 1; i <= count; i++) {
    districts.push({ value: String(i).padStart(2, "0"), label: `District ${i}` });
  }
  return districts;
}

/** API expects unpadded numeric districts ("5" not "05"); AL stays at-large. */
export function normalizeDistrictForApi(district) {
  if (!district) return null;
  const trimmed = String(district).trim();
  if (trimmed.toUpperCase() === "AL") return "AL";
  const parsed = parseInt(trimmed, 10);
  return Number.isFinite(parsed) ? String(parsed) : trimmed;
}

export const PARTY_COLORS = {
  DEM: "#3b82f6", REP: "#ef4444", IND: "#8b5cf6",
  LIB: "#f59e0b", GRN: "#10b981", D: "#3b82f6", R: "#ef4444",
};

export const PARTY_LABELS = {
  DEM: "Democrat", REP: "Republican", IND: "Independent",
  LIB: "Libertarian", GRN: "Green", D: "Democrat", R: "Republican",
};
